import React, { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const CertificationsSection = () => {
  const { user } = useAuth();
  const [certifications, setCertifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [formData, setFormData] = useState({
    certificationName: '',
    issuingOrganization: '',
    issueDate: '',
    expiryDate: '',
    certificationNumber: '',
  });
  const [error, setError] = useState('');

  useEffect(() => {
    if (user?.id) {
      loadCertifications();
    }
  }, [user]);

  const loadCertifications = async () => {
    try {
      setLoading(true);
      const { data, error: fetchError } = await supabase?.from('provider_certifications')?.select('*')?.eq('provider_id', user?.id)?.order('issue_date', { ascending: false });

      if (fetchError) throw fetchError;
      setCertifications(data || []);
    } catch (err) {
      setError(err?.message);
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async () => {
    try {
      setError('');
      const { error: insertError } = await supabase?.from('provider_certifications')?.insert({
        provider_id: user?.id,
        certification_name: formData?.certificationName,
        issuing_organization: formData?.issuingOrganization,
        issue_date: formData?.issueDate,
        expiry_date: formData?.expiryDate || null,
        certification_number: formData?.certificationNumber,
      });

      if (insertError) throw insertError;

      setFormData({
        certificationName: '',
        issuingOrganization: '',
        issueDate: '',
        expiryDate: '',
        certificationNumber: '',
      });
      setEditing(false);
      loadCertifications();
    } catch (err) {
      setError(err?.message);
    }
  };

  const handleDelete = async (certId) => {
    try {
      const { error: deleteError } = await supabase?.from('provider_certifications')?.delete()?.eq('id', certId)?.eq('provider_id', user?.id);

      if (deleteError) throw deleteError;
      loadCertifications();
    } catch (err) {
      console.error('Error deleting certification:', err);
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      active: { bg: 'bg-success/10', text: 'text-success', border: 'border-success/20', label: 'Actief' },
      expiring_soon: { bg: 'bg-warning/10', text: 'text-warning', border: 'border-warning/20', label: 'Verloopt Binnenkort' },
      expired: { bg: 'bg-error/10', text: 'text-error', border: 'border-error/20', label: 'Verlopen' },
    };
    return styles?.[status] || styles?.active;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString)?.toLocaleDateString('nl-NL', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  if (loading) {
    return (
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="animate-pulse space-y-4">
          {[1, 2, 3]?.map((i) => (
            <div key={i} className="h-24 bg-muted rounded"></div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-xl font-heading font-semibold text-foreground">Certificeringen</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Beheer uw professionele certificaten en kwalificaties
            </p>
          </div>
          {!editing && (
            <Button iconName="Plus" onClick={() => setEditing(true)}>
              Toevoegen
            </Button>
          )}
        </div>

        {error && (
          <div className="mb-4 p-4 bg-error/10 border border-error/20 rounded-md">
            <p className="text-sm text-error">{error}</p>
          </div>
        )}

        {/* Add Form */}
        {editing && (
          <div className="mb-6 p-4 bg-muted/30 rounded-lg space-y-4">
            <h3 className="font-medium text-foreground">Nieuwe Certificering</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Certificaat Naam"
                placeholder="Bijv. VCA Certificaat"
                value={formData?.certificationName}
                onChange={(e) => setFormData({ ...formData, certificationName: e?.target?.value })}
                required
              />
              <Input
                label="Uitgevende Organisatie"
                placeholder="Bijv. SSVV"
                value={formData?.issuingOrganization}
                onChange={(e) => setFormData({ ...formData, issuingOrganization: e?.target?.value })}
                required
              />
              <Input
                label="Uitgiftedatum"
                type="date"
                value={formData?.issueDate}
                onChange={(e) => setFormData({ ...formData, issueDate: e?.target?.value })}
                required
              />
              <Input
                label="Vervaldatum"
                type="date"
                value={formData?.expiryDate}
                onChange={(e) => setFormData({ ...formData, expiryDate: e?.target?.value })}
              />
              <Input
                label="Certificaatnummer"
                placeholder="Optioneel"
                value={formData?.certificationNumber}
                onChange={(e) => setFormData({ ...formData, certificationNumber: e?.target?.value })}
              />
            </div>
            <div className="flex items-center space-x-3">
              <Button onClick={handleAdd}>Opslaan</Button>
              <Button variant="outline" onClick={() => setEditing(false)}>
                Annuleren
              </Button>
            </div>
          </div>
        )}

        {/* Certifications List */}
        <div className="space-y-3">
          {certifications?.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Icon name="Award" size={48} className="mx-auto mb-3 opacity-50" />
              <p>Nog geen certificeringen toegevoegd</p>
            </div>
          ) : (
            certifications?.map((cert) => {
              const statusBadge = getStatusBadge(cert?.status);
              return (
                <div
                  key={cert?.id}
                  className="flex items-start justify-between p-4 bg-muted/30 rounded-lg hover:bg-muted/50 transition-colors"
                >
                  <div className="flex items-start space-x-4 flex-1">
                    <div className="bg-primary/10 p-3 rounded-lg">
                      <Icon name="Award" size={20} className="text-primary" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-1">
                        <h3 className="font-medium text-foreground">{cert?.certification_name}</h3>
                        <span
                          className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${
                            statusBadge?.bg
                          } ${statusBadge?.text} ${statusBadge?.border}`}
                        >
                          {statusBadge?.label}
                        </span>
                      </div>
                      <p className="text-sm text-muted-foreground mb-2">{cert?.issuing_organization}</p>
                      <div className="flex items-center space-x-4 text-xs text-muted-foreground">
                        <span>Uitgegeven: {formatDate(cert?.issue_date)}</span>
                        {cert?.expiry_date && <span>Verloopt: {formatDate(cert?.expiry_date)}</span>}
                        {cert?.certification_number && <span>Nr: {cert?.certification_number}</span>}
                      </div>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    iconName="Trash2"
                    onClick={() => handleDelete(cert?.id)}
                  />
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};

export default CertificationsSection;